import React from 'react';
import { useChat } from '../contexts/ChatContext';

const languageNames = {
  en: 'English',
  ar: 'Arabic',
  hi: 'Hindi',
  ur: 'Urdu',
  bn: 'Bengali',
  ta: 'Tamil',
  ml: 'Malayalam',
  te: 'Telugu'
};

const ChatMessage = ({ message, isLatestMessage = false }) => {
  const { role, content, metadata } = message;
  const { sendMessage } = useChat();
  
  // Debug: Log message metadata
  if (metadata) {
    console.log('[ChatMessage] Message metadata:', metadata);
  }

  const handleFollowUpClick = (opt) => {
    if (opt.type === 'pagination' && opt.text.match(/page\s*(\d+)/i)) {
      const pageMatch = opt.text.match(/page\s*(\d+)/i);
      const page = pageMatch ? parseInt(pageMatch[1]) : 1;
      sendMessage(JSON.stringify({ action: 'paginate', target: 'product_list', page }));
    } else if (opt.type === 'view' && /detailed/i.test(opt.text)) {
      sendMessage(JSON.stringify({ action: 'view', target: 'detailed_product_list' }));
    } else if (opt.type === 'view' && /categories/i.test(opt.text)) {
      sendMessage(JSON.stringify({ action: 'view', target: 'product_categories' }));
    } else if (opt.type === 'action' && /low stock/i.test(opt.text)) {
      sendMessage(JSON.stringify({ action: 'low_stock', target: 'product_list' }));
    } else if (opt.type === 'action' && /best selling/i.test(opt.text)) {
      sendMessage(JSON.stringify({ action: 'best_selling', target: 'product_list' }));
    } else if (opt.type === 'report' || /report/i.test(opt.text)) {
      sendMessage(JSON.stringify({ action: 'generate_report', target: 'product_report' }));
    } else {
      sendMessage(opt.text);
    }
  };

  const renderContent = (item) => {
    const language = item.language;
    const languageLabel = language ? languageNames[language] : null;
    if (item.type === 'options' && item.value && Array.isArray(item.value.options)) {
      return (
        <div className="flex flex-col gap-2">
          <div className="text-sm text-gray-600">{item.value.message || 'Please select an option:'}</div>
          <div className="flex flex-wrap gap-2">
            {item.value.options.map((option, i) => (
              <button
                key={option.id || i}
                onClick={() => sendMessage(option.text || option.name || option.id)}
                className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm hover:bg-blue-200 transition-colors cursor-pointer"
              >
                {option.name || option.id}
              </button>
            ))}
          </div>
        </div>
      );
    }
    switch (item.type) {
      case 'text':
        return (
          <div className="flex flex-col gap-1">
            <div className="whitespace-pre-wrap prose prose-sm max-w-none">
              {typeof item.value === 'string' 
                ? item.value.split('\n').map((line, i) => {
                    if (line.startsWith('**') && line.endsWith('**')) {
                      return <div key={i} className="font-bold text-gray-900 mb-1">{line.slice(2, -2)}</div>;
                    }
                    if (line.startsWith('📊') || line.startsWith('📅') || line.startsWith('🏪') || line.startsWith('💰') || line.startsWith('👥') || line.startsWith('💵') || line.startsWith('📊') || line.startsWith('❌')) {
                      return <div key={i} className="text-gray-800 mb-1">{line}</div>;
                    }
                    return line ? <div key={i} className="mb-1">{line}</div> : <br key={i} />;
                  })
                : JSON.stringify(item.value, null, 2)
              }
            </div>
            {languageLabel && (
              <div className="text-xs text-gray-500 italic">Detected language: {languageLabel}</div>
            )}
          </div>
        );
      case 'options':
        return (
          <div className="flex flex-col gap-2">
            <div className="text-sm text-gray-600">Available options:</div>
            <div className="flex flex-wrap gap-2">
              {Array.isArray(item.value) && item.value.map((option, i) => (
                <button
                  key={i}
                  onClick={() => sendMessage(option)}
                  className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm hover:bg-blue-200 transition-colors cursor-pointer"
                >
                  {option}
                </button>
              ))}
            </div>
            {isLatestMessage && typeof item.metadata === "object" && item.metadata !== null && 
             item.metadata.followUpOptions && item.metadata.followUpOptions.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {item.metadata.followUpOptions.map((opt, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleFollowUpClick(opt)}
                    className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm hover:bg-green-200 transition-colors cursor-pointer"
                  >
                    {opt.text}
                  </button>
                ))}
              </div>
            )}
            {languageLabel && (
              <div className="text-xs text-gray-500 italic">Detected language: {languageLabel}</div>
            )}
          </div>
        );
      case 'table':
        return (
          <div className="flex flex-col gap-2">
            <div className="text-sm text-gray-600">Current stock levels:</div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stock</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {Array.isArray(item.value) && item.value.map((row, i) => (
                    <tr key={i}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.name}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.stock}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {languageLabel && (
              <div className="text-xs text-gray-500 italic">Detected language: {languageLabel}</div>
            )}
          </div>
        );
      case 'confirmation':
        return (
          <div className="flex flex-col gap-1">
            <div className="text-green-600">{item.value}</div>
            {languageLabel && (
              <div className="text-xs text-gray-500 italic">Detected language: {languageLabel}</div>
            )}
          </div>
        );
      default:
        return <div className="text-gray-600">{String(item.value)}</div>;
    }
  };

  return (
    <div className={`flex ${role === 'assistant' ? 'justify-start' : 'justify-end'} mb-4`}>
      <div className={`max-w-[80%] rounded-lg px-4 py-2 ${
        role === 'assistant' 
          ? 'bg-blue-100 text-blue-900' 
          : 'bg-gray-100 text-gray-900'
      }`}>
        {content.map((item, i) => (
          <div key={i} className="mb-2 last:mb-0">
            {renderContent(item)}
          </div>
        ))}
        {/* Show extracted metadata for debugging */}
        {metadata && isLatestMessage && role === 'user' && (
          <div className="mt-2 pt-2 border-t border-gray-300 text-xs">
            <div className="font-semibold text-gray-600 mb-1">Extracted Info:</div>
            {metadata.locations && Array.isArray(metadata.locations) && metadata.locations.length > 0 && (
              <div className="text-gray-600">📍 Locations: {metadata.locations.join(', ')}</div>
            )}
            {metadata.startDate && (
              <div className="text-gray-600">📅 From: {metadata.startDate}</div>
            )}
            {metadata.endDate && (
              <div className="text-gray-600">📅 To: {metadata.endDate}</div>
            )}
            {metadata.intent && (
              <div className="text-gray-600">🎯 Intent: {metadata.intent}</div>
            )}
            {metadata.confidence && (
              <div className="text-gray-600">📊 Confidence: {Math.round(metadata.confidence * 100)}%</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatMessage; 